import { supabase } from './supabase'

export type HighlightMode = 'recognition' | 'ranking'

export type HighlightUser = {
  id: string
  nome: string
  email: string
  default_unit_key: string | null
}

export type HighlightItem = {
  user_id: string
  nome: string
  email: string
  unit_key: string | null
  unit_label: string
  position: number
  reason: string
}

export type ActiveHighlight = {
  id: number
  title: string
  subtitle: string | null
  mode: HighlightMode
  starts_at: string
  ends_at: string | null
  items: HighlightItem[]
}

export type HighlightManagement = ActiveHighlight & {
  status: 'ativo' | 'encerrado'
  created_at: string
  created_by_name: string | null
}

export async function loadHighlightUsers(): Promise<HighlightUser[]> {
  const { data, error } = await supabase.rpc('highlight_eligible_users')
  if (error) throw error
  return (data ?? []) as HighlightUser[]
}

export async function loadActiveHighlight(): Promise<ActiveHighlight | null> {
  const { data, error } = await supabase.rpc('active_highlight')
  if (error) throw error
  return (data ?? null) as ActiveHighlight | null
}

export async function loadHighlightManagement(): Promise<HighlightManagement[]> {
  const { data, error } = await supabase.rpc('highlight_management')
  if (error) throw error
  return (data ?? []) as HighlightManagement[]
}

export async function createHighlight(input: {
  title: string
  subtitle?: string
  mode: HighlightMode
  startsAt?: string | null
  endsAt?: string | null
  items: Array<{ userId: string; reason: string; position: number }>
}) {
  const { data, error } = await supabase.rpc('create_highlight', {
    p_title: input.title,
    p_subtitle: input.subtitle ?? '',
    p_starts_at: input.startsAt || null,
    p_ends_at: input.endsAt || null,
    p_items: input.items.map((item) => ({
      user_id: item.userId,
      reason: item.reason,
      position: item.position,
    })),
    p_mode: input.mode,
  })
  if (error) throw error
  return data as number
}

export async function closeHighlight(id: number) {
  const { error } = await supabase.rpc('close_highlight', { p_highlight_id: id })
  if (error) throw error
}

export async function deleteHighlight(id: number) {
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession()
  if (sessionError) throw sessionError

  const token = sessionData.session?.access_token
  if (!token) throw new Error('Sessão inválida ou expirada.')

  const response = await fetch('/api/admin/highlights', {
    method: 'DELETE',
    headers: {
      authorization: `Bearer ${token}`,
      'content-type': 'application/json',
    },
    body: JSON.stringify({ id }),
  })

  let payload: { error?: string } = {}
  try {
    payload = await response.json() as { error?: string }
  } catch {
    payload = {}
  }

  if (!response.ok) {
    throw new Error(payload.error || 'Não foi possível excluir definitivamente o histórico de destaque.')
  }
}
